import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  ClickAwayListener,
  Portal,
  Paper,
  Typography,
} from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * Date range picker — popup opens above the field so hero overflow / blue scrim
 * cannot hide the calendars.
 */
const DateRangeInput = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  minDate = new Date(),
  disabled = false,
  emptyLabel = 'Select dates',
  endLabel = 'Return',
  ...props
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const anchorRef = useRef(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  const normalizedMin = useMemo(() => {
    const d = minDate instanceof Date ? new Date(minDate) : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [minDate]);

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const displayText = startDate
    ? endDate
      ? `${formatDate(startDate)} — ${formatDate(endDate)}`
      : `${formatDate(startDate)} — ${endLabel}`
    : emptyLabel;

  const openCalendar = () => {
    if (disabled) return;
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) {
      const popupWidth = Math.min(620, window.innerWidth - 24);
      let left = rect.left;
      if (left + popupWidth > window.innerWidth - 12) {
        left = Math.max(12, window.innerWidth - popupWidth - 12);
      }
      // Open upward from the field
      setPopupPos({
        top: rect.top + window.scrollY - 8,
        left: left + window.scrollX,
        width: popupWidth,
        openUp: true,
      });
    }
    setShowCalendar(true);
  };

  useEffect(() => {
    if (!showCalendar) return undefined;
    const onScroll = () => setShowCalendar(false);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [showCalendar]);

  return (
    <Box sx={{ position: 'relative' }} ref={anchorRef}>
      <TextField
        {...props}
        value={displayText}
        placeholder={emptyLabel}
        disabled={disabled}
        fullWidth
        variant="outlined"
        size="medium"
        readOnly
        onClick={openCalendar}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <DateRangeIcon sx={{ color: 'primary.main' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          cursor: 'pointer',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'white',
            height: 52,
          },
        }}
      />

      {showCalendar && (
        <Portal>
          <ClickAwayListener onClickAway={() => setShowCalendar(false)}>
            <Paper
              elevation={8}
              sx={{
                position: 'absolute',
                top: popupPos.openUp ? undefined : popupPos.top,
                bottom: popupPos.openUp
                  ? `${window.innerHeight - (anchorRef.current?.getBoundingClientRect().top || 0) + 8}px`
                  : undefined,
                left: popupPos.left,
                width: { xs: 'calc(100vw - 24px)', sm: popupPos.width || 620 },
                maxWidth: 'calc(100vw - 24px)',
                zIndex: 1400,
                p: 2,
                borderRadius: 2,
                bgcolor: '#fff',
                transform: popupPos.openUp ? 'translateY(0)' : undefined,
              }}
              style={
                popupPos.openUp
                  ? {
                      position: 'fixed',
                      bottom: Math.max(
                        12,
                        window.innerHeight - (anchorRef.current?.getBoundingClientRect().top || 0) + 8
                      ),
                      left: popupPos.left - window.scrollX,
                      top: 'auto',
                    }
                  : {
                      position: 'absolute',
                      top: popupPos.top,
                      left: popupPos.left,
                    }
              }
            >
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Select depart date, then return (optional — click away for one-way)
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  '& .react-datepicker': {
                    border: 'none',
                    fontFamily: 'inherit',
                    bgcolor: '#fff',
                  },
                  '& .react-datepicker__header': {
                    backgroundColor: '#1F6F78',
                    borderBottom: 'none',
                    borderRadius: '8px 8px 0 0',
                    paddingTop: '12px',
                  },
                  '& .react-datepicker__current-month, & .react-datepicker__day-name': {
                    color: '#fff !important',
                  },
                  '& .react-datepicker__navigation-icon::before': {
                    borderColor: '#fff',
                  },
                  '& .react-datepicker__day': {
                    color: '#0E1C2A',
                  },
                  '& .react-datepicker__day--disabled': {
                    color: '#bbb !important',
                  },
                  '& .react-datepicker__day--selected, & .react-datepicker__day--in-range, & .react-datepicker__day--keyboard-selected':
                    {
                      backgroundColor: '#1F6F78 !important',
                      color: '#fff !important',
                    },
                  '& .react-datepicker__day--today': {
                    fontWeight: 700,
                    border: '1px solid #1F6F78',
                  },
                  '& .react-datepicker__day:hover': {
                    backgroundColor: 'rgba(31, 111, 120, 0.12)',
                  },
                }}
              >
                <Box>
                  <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block' }}>
                    Depart
                  </Typography>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => {
                      onStartDateChange(date);
                      if (date && endDate && date > endDate) {
                        onEndDateChange(null);
                      }
                    }}
                    minDate={normalizedMin}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    inline
                    monthsShown={1}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block' }}>
                    Return
                  </Typography>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => {
                      if (startDate && date >= startDate) {
                        onEndDateChange(date);
                        setShowCalendar(false);
                      }
                    }}
                    minDate={startDate || normalizedMin}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    inline
                    disabled={!startDate}
                    monthsShown={1}
                  />
                </Box>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Portal>
      )}
    </Box>
  );
};

export default DateRangeInput;
